import { format } from "date-fns";
import {
  getTournamentMatchIds,
  getTournamentMatchTitle,
} from "model/Tournament/Tournament";
import { RootState } from "app/store";
import { nonUndefined, onlyUnique } from "Utility";
import { selectTourTitleDisplayForTournamentId } from "./Tour";
import {
  selectAllTournaments,
  selectTournament,
  selectTournaments,
} from "./Tournament";
import {
  Match,
  getMatchDriverIds,
  getWinningRosterIds,
  matchContainsRoster,
  matchContainsTeam,
} from "model/Match/Match";
import { considerMatchLiveOrUpcoming } from "model/Match/MatchStatus";
import {
  getDefinedRosterIds,
  getRoster,
  getRosterDriverIds,
  getRosterId,
} from "model/Roster/Roster";

export const selectAllMatchIds = (state: RootState) => {
  return Object.keys(state.match.data);
};

export const selectMatch = (state: RootState, id: string | undefined) => {
  if (!id) {
    return undefined;
  }

  return state.match.data[id];
};

export const selectMatches = (state: RootState, ids: string[]) =>
  nonUndefined(ids.map((id) => selectMatch(state, id)));

export const selectMatchStartTime = (state: RootState, matchId: string) =>
  selectMatch(state, matchId)?.startTimeMs;

export const selectMatchTeams = (state: RootState, matchId: string) =>
  selectMatch(state, matchId)?.teams;

export const selectMatchRosterIds = (state: RootState, matchId: string) => {
  const teams = selectMatchTeams(state, matchId);
  if (!teams) {
    return undefined;
  }

  return getDefinedRosterIds(teams.rosters);
};

export const selectMatchDriverIds = (state: RootState, matchId: string) => {
  const match = selectMatch(state, matchId);
  if (!match) {
    return undefined;
  }

  return getMatchDriverIds(match);
};

export const selectMatchRosterScope = (state: RootState, matchId: string) =>
  selectMatch(state, matchId)?.teams.scope;

export const selectMatchRosterDriverIds = (
  state: RootState,
  matchId: string,
  rosterId: string
) => {
  const match = selectMatch(state, matchId);
  if (!match) {
    return undefined;
  }

  const roster = getRoster(match.teams.rosters, rosterId);
  if (!roster) {
    return undefined;
  }

  return getRosterDriverIds(roster, match.teams.scope);
};

export const selectTournamentIdFromMatchId = (
  state: RootState,
  matchId: string
) => {
  const lookupId = state.match.tournamentIdLookup[matchId];
  if (!!lookupId) {
    return lookupId;
  }

  const tournaments = selectAllTournaments(state);
  for (var i = 0; i < tournaments.length; i++) {
    const tournament = tournaments[i];
    if (getTournamentMatchIds(tournament).includes(matchId)) {
      return tournament.id;
    }
  }

  return undefined;
};

export const selectTeamMatches = (
  state: RootState,
  matchIds: string[],
  teamId: string
) => {
  return selectMatches(state, matchIds).filter((m) =>
    matchContainsTeam(m, teamId)
  );
};

export const selectMatchesByRecordSwiss = (
  state: RootState,
  ids: string[],
  priorIds: string[]
) => {
  const matchesByPriorRecord = new Map<
    { wins: number; loses: number },
    Match[]
  >();

  const matches = selectMatches(state, ids);
  const priorMatches = selectMatches(state, priorIds);
  matches.forEach((m) => {
    var wins = 0;
    var loses = 0;
    const rosterOne = m.teams.rosters.at(0);
    const rosterOneId = !!rosterOne ? getRosterId(rosterOne) : undefined;

    if (!!rosterOneId) {
      const recordMatches = priorMatches.filter((p) =>
        matchContainsRoster(p, rosterOneId)
      );

      recordMatches.forEach((r) => {
        const winningRosterIds = getWinningRosterIds(r);
        if (!!winningRosterIds) {
          if (winningRosterIds.includes(rosterOneId)) {
            wins++;
          } else {
            loses++;
          }
        }
      });
    }

    const key = Array.from(matchesByPriorRecord.keys()).find(
      (k) => k.wins === wins && k.loses === loses
    );
    if (key) {
      matchesByPriorRecord.get(key)!.push(m);
    } else {
      matchesByPriorRecord.set({ wins, loses }, [m]);
    }
  });

  return matchesByPriorRecord;
};

export const selectMatchIdsFromTournamentIds = (
  state: RootState,
  tournamentIds: string[]
) => {
  const tournaments = selectTournaments(state, tournamentIds);
  return tournaments
    .flatMap((t) => getTournamentMatchIds(t))
    .filter(onlyUnique);
};

export const selectCompletedMatches = (
  state: RootState,
  matchIds: string[]
) => {
  return selectMatches(state, matchIds).filter(
    (m) => m.isComplete || !considerMatchLiveOrUpcoming(m.startTimeMs)
  );
};

export const selectNonCompletedMatches = (
  state: RootState,
  matchIds: string[]
) => {
  return selectMatches(state, matchIds).filter(
    (m) => considerMatchLiveOrUpcoming(m.startTimeMs) && !m.isComplete
  );
};

export const selectRecentMatchesPage = (
  state: RootState,
  amount: number | undefined
) => {
  const matchIds = selectAllMatchIds(state);
  const completeMatches = selectCompletedMatches(state, matchIds);
  const sortedMatches = completeMatches.sort(
    (a, b) => b.startTimeMs - a.startTimeMs
  );

  return {
    matches: sortedMatches.slice(0, amount),
    totalAmount: sortedMatches.length,
  };
};

export const selectRecentMatches = (
  state: RootState,
  tournamentIds?: string[],
  amount?: number
) => {
  const matchIds = !tournamentIds
    ? selectAllMatchIds(state)
    : selectMatchIdsFromTournamentIds(state, tournamentIds);
  const completeMatches = selectCompletedMatches(state, matchIds);
  const sortedMatches = completeMatches.sort(
    (a, b) => b.startTimeMs - a.startTimeMs
  );

  return sortedMatches.slice(0, amount);
};

export const selectUpcomingMatches = (
  state: RootState,
  amount: number,
  tournamentIds?: string[]
) => {
  const matchIds = !tournamentIds
    ? selectAllMatchIds(state)
    : selectMatchIdsFromTournamentIds(state, tournamentIds);
  const incompleteMatches = selectNonCompletedMatches(state, matchIds);

  const sortedMatches = incompleteMatches.sort(
    (a, b) => a.startTimeMs - b.startTimeMs
  );

  return sortedMatches.slice(0, amount);
};

export const selectUpcomingMatchesByTime = (
  state: RootState,
  latestStartTime: number
) => {
  const incompleteMatches = selectNonCompletedMatches(
    state,
    selectAllMatchIds(state)
  );

  return incompleteMatches.filter((m) => m.startTimeMs < latestStartTime);
};

export const selectMatchSummaryFromMatchId = (
  state: RootState,
  matchId: string
) => {
  const match = selectMatch(state, matchId);
  if (!match) {
    return undefined;
  }

  const dateString =
    new Date(match.startTimeMs).toDateString() +
    format(new Date(match.startTimeMs), " HH:mm");
  const tournamentId = selectTournamentIdFromMatchId(state, matchId);
  const tournament = !!tournamentId
    ? selectTournament(state, tournamentId)
    : undefined;
  if (!tournament) {
    return `Unknown Tournament - ${dateString}`;
  }

  const titleWithTournament = `${tournament.name} ${
    getTournamentMatchTitle(tournament, matchId, true) ?? ""
  } - ${dateString}`;
  const tourTitle = selectTourTitleDisplayForTournamentId(state, tournament.id);
  return !!tourTitle
    ? `${tourTitle} - ${titleWithTournament}`
    : titleWithTournament;
};

export const selectAnyMatchHasDefinedRoster = (
  state: RootState,
  matchIds: string[]
) => {
  for (let i = 0; i < matchIds.length; i++) {
    const matchId = matchIds[i];
    const match = selectMatch(state, matchId);
    if (!!match && getDefinedRosterIds(match.teams.rosters).length > 0) {
      return true;
    }
  }

  return false;
};

export const selectMatchesRosterIds = (
  state: RootState,
  matchIds: string[]
) => {
  return matchIds
    .flatMap((id) => selectMatchRosterIds(state, id) ?? [])
    .filter(onlyUnique);
};
